import {
  Layers,
  FileUp,
  Globe,
  Sparkles,
  Type,
  FileText,
  File,
  Upload,
  Link2,
  Settings,
  ScanLine,
  Files,
  Brain,
  Search,
  Bot,
  Boxes,
  CheckCircle,
  Loader2,
} from "lucide-react"

interface Feature {
  icon: React.ReactNode
  title: string
  description: string
}

interface FeatureShowcase {
  badge: string
  badgeIcon: React.ReactNode
  title: string
  description: string
  features: Feature[]
  mockup: React.ReactNode
  reversed?: boolean
}

/**
 * Content Type Tabs Mockup component.
 * Displays tabs for Text, Files, and Website content types.
 */
function ContentTypesMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Tab Headers */}
      <div className="flex gap-1 mb-4 border-b border-neutral-200">
        {[
          { icon: <Type className="h-4 w-4" />, label: "Text", active: true },
          { icon: <FileText className="h-4 w-4" />, label: "Files", active: false },
          { icon: <Globe className="h-4 w-4" />, label: "Website", active: false },
        ].map((tab, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium -mb-px ${
              tab.active
                ? "text-primary border-b-2 border-primary"
                : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            {tab.icon}
            {tab.label}
          </div>
        ))}
      </div>

      {/* Text Content Area */}
      <div className="space-y-3">
        <div className="text-xs text-neutral-500 mb-1">Content *</div>
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 min-h-[120px]">
          <div className="space-y-2 font-mono text-sm text-neutral-600">
            <div>Our company was founded in 2020 with a mission to democratize AI...</div>
            <div className="text-neutral-400">|</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <span>Supports markdown formatting</span>
          <span>1,234 / 50,000 characters</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-4 flex gap-2">
        {["Company Info", "Product Details", "FAQs"].map((label, i) => (
          <div
            key={i}
            className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs text-neutral-600 hover:border-primary/30 hover:bg-primary/5 transition-colors cursor-pointer"
          >
            + {label}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * File Upload Mockup component.
 * Displays a drag-and-drop file upload interface.
 */
function FileUploadMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Dropzone */}
      <div className="rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-8 text-center mb-4">
        <div className="flex justify-center mb-3">
          <div className="rounded-full bg-primary/10 p-3">
            <Upload className="h-6 w-6 text-primary" />
          </div>
        </div>
        <div className="text-sm font-medium text-neutral-700 mb-1">
          Drop files here or click to upload
        </div>
        <div className="text-xs text-neutral-500">
          PDF, DOCX, DOC, TXT (max 10MB each)
        </div>
      </div>

      {/* Uploaded Files List */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-neutral-500 mb-2">Uploaded Files</div>
        {[
          { name: "employee-handbook.pdf", size: "2.4 MB", status: "complete" },
          { name: "product-specs.docx", size: "1.1 MB", status: "complete" },
          { name: "training-guide.pdf", size: "3.2 MB", status: "processing" },
        ].map((file, i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2"
          >
            <div className="flex items-center gap-3">
              <div className="rounded bg-blue-500/10 p-1.5">
                <File className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <div className="text-sm text-neutral-700">{file.name}</div>
                <div className="text-xs text-neutral-500">{file.size}</div>
              </div>
            </div>
            {file.status === "complete" ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <Loader2 className="h-4 w-4 text-primary animate-spin" />
            )}
          </div>
        ))}
      </div>

      {/* Upload Stats */}
      <div className="mt-4 flex items-center justify-between text-xs text-neutral-500">
        <span>3 of 5 files uploaded</span>
        <span className="text-primary">Process All</span>
      </div>
    </div>
  )
}

/**
 * Website Crawl Mockup component.
 * Displays website URL input with crawl configuration.
 */
function WebsiteCrawlMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* URL Input */}
      <div className="mb-4">
        <div className="text-xs text-neutral-500 mb-1">Website URL *</div>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2">
            <Link2 className="h-4 w-4 text-neutral-400" />
            <span className="text-sm text-neutral-700">https://docs.example.com</span>
          </div>
          <div className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white">
            Fetch
          </div>
        </div>
      </div>

      {/* Crawl Settings */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-xs text-neutral-500 mb-1">Crawl Depth</div>
          <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
            <Settings className="h-4 w-4 text-neutral-400" />
            <span className="text-sm text-neutral-700">2 levels</span>
          </div>
        </div>
        <div>
          <div className="text-xs text-neutral-500 mb-1">Max Pages</div>
          <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
            <Files className="h-4 w-4 text-neutral-400" />
            <span className="text-sm text-neutral-700">50 pages</span>
          </div>
        </div>
      </div>

      {/* Discovered Links */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-neutral-500">Discovered Links</span>
          <span className="text-xs text-primary">Select All</span>
        </div>
        <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
          {[
            { url: "/getting-started", selected: true },
            { url: "/api-reference", selected: true },
            { url: "/tutorials/basics", selected: true },
            { url: "/tutorials/advanced", selected: false },
            { url: "/changelog", selected: false },
          ].map((link, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                link.selected
                  ? "bg-primary/5 border border-primary/20 text-primary"
                  : "bg-neutral-50 border border-neutral-200 text-neutral-600"
              }`}
            >
              <div
                className={`h-4 w-4 rounded border flex items-center justify-center ${
                  link.selected
                    ? "border-primary bg-primary"
                    : "border-neutral-300 bg-white"
                }`}
              >
                {link.selected && <CheckCircle className="h-3 w-3 text-white" />}
              </div>
              <Globe className="h-3.5 w-3.5" />
              <span>{link.url}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Embeddings Mockup component.
 * Displays content chunks with vector embedding visualization.
 */
function EmbeddingsMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-purple-500/10 p-2">
            <Brain className="h-5 w-5 text-purple-500" />
          </div>
          <div>
            <div className="text-sm font-medium text-neutral-700">AI Processing</div>
            <div className="text-xs text-neutral-500">Automatic chunking & embeddings</div>
          </div>
        </div>
        <div className="rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-600">
          Ready
        </div>
      </div>

      {/* Content Chunks */}
      <div className="space-y-2 mb-4">
        <div className="text-xs font-medium text-neutral-500">Content Chunks</div>
        {[
          { text: "Our company was founded in 2020 with a mission to democratize AI for businesses...", tokens: 128 },
          { text: "We offer three main products: Agent Studio for building AI agents, Workflow Builder...", tokens: 96 },
          { text: "Our team consists of AI researchers, engineers, and designers from leading tech...", tokens: 112 },
        ].map((chunk, i) => (
          <div
            key={i}
            className="rounded-lg border border-neutral-200 bg-neutral-50 p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-neutral-600 line-clamp-2">{chunk.text}</div>
              <div className="shrink-0 flex items-center gap-2">
                <span className="rounded bg-purple-500/10 px-1.5 py-0.5 text-xs font-medium text-purple-600">
                  1536D
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-neutral-500">
              <span>{chunk.tokens} tokens</span>
              <span className="h-1 w-1 rounded-full bg-neutral-300" />
              <span className="text-green-600">Embedded</span>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between rounded-lg bg-primary/5 border border-primary/20 px-4 py-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-primary" />
          <span className="text-sm text-primary font-medium">Semantic Search Ready</span>
        </div>
        <div className="flex items-center gap-1">
          <Bot className="h-4 w-4 text-primary" />
          <span className="text-xs text-primary">Connect to Agent</span>
        </div>
      </div>
    </div>
  )
}

const showcases: FeatureShowcase[] = [
  {
    badge: "Multiple Content Types",
    badgeIcon: <Layers className="h-3.5 w-3.5" />,
    title: "One Place for All Your Knowledge",
    description:
      "Import knowledge from multiple sources and organize it in one unified platform. Whether it's company documents, website content, or plain text - we handle it all.",
    features: [
      {
        icon: <Type className="h-5 w-5" />,
        title: "Rich Text Content",
        description: "Add text with full markdown support",
      },
      {
        icon: <FileText className="h-5 w-5" />,
        title: "Document Upload",
        description: "PDF, DOCX, DOC, and TXT files",
      },
      {
        icon: <Globe className="h-5 w-5" />,
        title: "Website Extraction",
        description: "Crawl and import web content",
      },
      {
        icon: <Search className="h-5 w-5" />,
        title: "Organized & Searchable",
        description: "Find any knowledge instantly",
      },
    ],
    mockup: <ContentTypesMockup />,
  },
  {
    badge: "Smart File Processing",
    badgeIcon: <FileUp className="h-3.5 w-3.5" />,
    title: "Drop Files, We Handle the Rest",
    description:
      "Upload documents and let AI automatically extract the knowledge. Support for multiple file formats with intelligent content extraction.",
    features: [
      {
        icon: <Upload className="h-5 w-5" />,
        title: "Drag & Drop Upload",
        description: "Simple file upload experience",
      },
      {
        icon: <ScanLine className="h-5 w-5" />,
        title: "OCR Support",
        description: "Extract text from scanned PDFs",
      },
      {
        icon: <FileText className="h-5 w-5" />,
        title: "Word Extraction",
        description: "Full DOCX & DOC support",
      },
      {
        icon: <Files className="h-5 w-5" />,
        title: "Batch Processing",
        description: "Up to 5 files, 10MB each",
      },
    ],
    mockup: <FileUploadMockup />,
    reversed: true,
  },
  {
    badge: "Website Crawling",
    badgeIcon: <Globe className="h-3.5 w-3.5" />,
    title: "Import Knowledge from Any Website",
    description:
      "Automatically crawl and extract content from websites. Configure crawl depth, select specific pages, and let our AI clean and structure the content.",
    features: [
      {
        icon: <Link2 className="h-5 w-5" />,
        title: "Automatic Discovery",
        description: "Find all linked pages automatically",
      },
      {
        icon: <Settings className="h-5 w-5" />,
        title: "Configurable Depth",
        description: "1-3 levels of crawl depth",
      },
      {
        icon: <Files className="h-5 w-5" />,
        title: "Page Limit Control",
        description: "Limit from 10 to 100 pages",
      },
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: "Content Cleaning",
        description: "AI removes navigation & ads",
      },
    ],
    mockup: <WebsiteCrawlMockup />,
  },
  {
    badge: "AI-Powered Embeddings",
    badgeIcon: <Sparkles className="h-3.5 w-3.5" />,
    title: "AI-Ready Knowledge",
    description:
      "Your knowledge is automatically processed for AI consumption. Content is chunked, embedded, and indexed for semantic search and agent integration.",
    features: [
      {
        icon: <Boxes className="h-5 w-5" />,
        title: "Automatic Chunking",
        description: "Optimal chunk sizes for AI",
      },
      {
        icon: <Brain className="h-5 w-5" />,
        title: "Vector Embeddings",
        description: "1536-dimensional vectors",
      },
      {
        icon: <Search className="h-5 w-5" />,
        title: "Semantic Search",
        description: "Find by meaning, not keywords",
      },
      {
        icon: <Bot className="h-5 w-5" />,
        title: "Agent Integration",
        description: "Connect directly to AI agents",
      },
    ],
    mockup: <EmbeddingsMockup />,
    reversed: true,
  },
]

/**
 * Features section displaying the four main knowledge base capabilities.
 * Uses alternating left/right layout with mockups and feature lists.
 */
export function FeaturesSection() {
  return (
    <section className="bg-neutral-50 py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            Everything You Need to Build Knowledge Bases
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            Import content from any source, let AI process it, and give your agents
            the context they need to deliver accurate, helpful responses.
          </p>
        </div>

        {/* Feature Showcases */}
        <div className="space-y-24">
          {showcases.map((showcase, index) => (
            <div
              key={index}
              className={`grid gap-12 lg:grid-cols-2 lg:gap-16 ${
                showcase.reversed ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Text Content */}
              <div
                className={`flex flex-col justify-center ${
                  showcase.reversed ? "lg:order-2" : ""
                }`}
              >
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
                  {showcase.badgeIcon}
                  {showcase.badge}
                </div>
                <h3 className="mb-4 text-2xl font-bold text-neutral-900 lg:text-3xl">
                  {showcase.title}
                </h3>
                <p className="mb-8 text-neutral-600">{showcase.description}</p>

                {/* Feature List */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {showcase.features.map((feature, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        {feature.icon}
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">
                          {feature.title}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mockup */}
              <div className={showcase.reversed ? "lg:order-1" : ""}>
                {showcase.mockup}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
